import React, { useEffect, useContext, useRef, useState } from "react";
import MapMenu from "./MapMenu";
import MapContext from "../MapContext";
import { toLonLat, fromLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { Modify } from "ol/interaction";
import { Icon, Style } from "ol/style";
import EagleViewIcon from "../../icons/eagle-view-icon.png";
import { siteAPI } from "../../api/axios";
import { useSelector } from "react-redux";
import "./LinkedMaps.css";

const ipaLoadUrl = "https://pol.pictometry.com/ipa/v1/load.php";
const iFrameId = "picto";

export const Eagle = ({
    setActiveMaps,
    activeMaps,
    activeWindows,
    setActiveWindows,
    projection,
    startingPoint,
}) => {
    const { map } = useContext(MapContext);
    const pictoRef = useRef();
    const markerRef = useRef();
    const centerLonLat = toLonLat(startingPoint, projection);
    const apiKey = useSelector(
        (state) => state.configData?.thirdPartyMaps?.Eagleview?.APIKey
    );
    const [signedUrl, setSignedUrl] = useState();
    const pictoUrl = `https://pol.pictometry.com/ipa/v1/embed/host.php?apikey=${apiKey}`;

    const getSignedUrl = async () => {
        const res = await siteAPI.get(`/EagleView/${apiKey}?frameId=${iFrameId}`);
        const url = res.data.signedUrl;
        setSignedUrl(url);
    };

    const centerMap = () => {
        const newCenter = [
            ...markerRef.current.getSource().getFeatures()[0].getGeometry()
                .flatCoordinates,
        ];
        map.getView().animate({ center: newCenter });
    };

    useEffect(() => {
        getSignedUrl();
        // Create Pictometry script
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = pictoUrl;
        script.onload = () => {
            const Pictometry = window.PictometryHost;
            const pictometry = new Pictometry(iFrameId, ipaLoadUrl);
            initializePictometry(pictometry, markerRef, projection, centerLonLat);
            pictoRef.current = pictometry;
        };
        // Attach script to head of document
        const head = document.querySelector("head");
        head.appendChild(script);

        //Add Marker
        const feature = new Feature({
            geometry: new Point(startingPoint),
            name: "Eagle-View Marker",
        });
        feature.setStyle(iconStyle);
        const source = new VectorSource({
            features: [feature],
        });
        markerRef.current = new VectorLayer({
            source: source,
            zIndex: 1000,
        });
        map.addLayer(markerRef.current);

        // Add Interaction
        const modify = new Modify({
            source: source,
        });
        map.addInteraction(modify);
        modify.on("modifyend", (e) => updateEagleViewWithDrag(e, projection, pictoRef));

        return () => {
            head.removeChild(script);
            map.removeInteraction(modify);
            map.removeLayer(markerRef.current);
        };
    }, []);

    return (
        <>
            {signedUrl ? <EagleIframe signedUrl={signedUrl} /> : <h3>Loading...</h3>}
            <MapMenu
                setActiveMaps={setActiveMaps}
                activeMaps={activeMaps}
                mapName={"eagle"}
                setActiveWindows={setActiveWindows}
                activeWindows={activeWindows}
                centerMap={centerMap}
            />
        </>
    );
};

export const Iframe = ({ signedUrl }) => {
    return (
        <iframe
            id={iFrameId}
            title="Eagle View"
            src={signedUrl}
            style={{ width: "100%", height: "100%" }}
        />
    );
};

//Memoize iframe to prevent rerendering (only rerenders in signedUrl changes)
export const EagleIframe = React.memo(Iframe);

const updateEagleViewWithDrag = (e, projection, pictoRef) => {
    const olCoords = e.features.array_[0].getGeometry().getCoordinates();
    const coordsLonLat = toLonLat(olCoords, projection);
    pictoRef.current.setLocation(coordsLonLat[1], coordsLonLat[0]);
};

const initializePictometry = (pictometry, markerRef, projection, centerLonLat) => {
    pictometry.ready = () => {
        pictometry.setPreferences({
            enableSynchronization: true,
        });
        pictometry.addListener("error", (error) => {
            console.log("Error: ", +JSON.stringify(error));
        });
        pictometry.addListener("synchronize", (view) => {
            if (view?.x != null && view?.y != null) {
                const markerGeom = markerRef.current
                    .getSource()
                    .getFeatures()[0]
                    .getGeometry();
                const newCoords = fromLonLat([view.x, view.y], projection);
                markerGeom.setCoordinates(newCoords);
            }
        });
        pictometry.setLocation(centerLonLat[1], centerLonLat[0]);
    };
};

const iconStyle = new Style({
    image: new Icon({
        anchor: [0.55, 175],
        anchorXUnits: "fraction",
        anchorYUnits: "pixels",
        src: EagleViewIcon,
        zIndex: 1000,
        scale: 0.3,
    }),
});
